/**
 * Client HTTP du backend ReverseTrader.
 *
 * Tous les appels utilisent des chemins RELATIFS (`/api/...`) : en développement
 * le serveur Vite les relaie vers FastAPI, en production Caddy fait la même
 * chose. Aucune URL de backend n'est donc codée en dur nulle part.
 */

import type {
  AccountsState,
  BrokerStatus,
  CTraderStatus,
  Candle,
  InstrumentsResponse,
  PlanRequest,
  GuardianStatus,
  OrderResponse,
  PlanResponse,
  BrokerAccountOption,
  CreateUserBody,
  ManagedUser,
  Position,
  StatsResponse,
  TradingAccountAdmin,
  UpdateUserBody,
} from './types'

/** Réponse des routes de session : qui est connecté, et avec quel rôle. */
export interface SessionInfo {
  authenticated: boolean
  username: string | null
  /**
   * `admin` ou `trader`. Sert uniquement à décider ce qu'on AFFICHE : chaque
   * route d'administration revérifie le rôle côté serveur, un client modifié
   * ne gagne rien à mentir ici.
   */
  role: 'admin' | 'trader' | null
}

/** Réponse de `/api/health`. */
export interface Health {
  status: string
  version: string
  app_env: string
  ctrader_env: 'demo' | 'live'
  live_trading: boolean
  broker_configured: boolean
  broker_connected: boolean
}

/** Réponse de `/api/config` : configuration non sensible. */
export interface PublicConfig {
  account_currency: string
  default_leverage: number
  ctrader_env: 'demo' | 'live'
  live_trading: boolean
  broker_configured: boolean
  risk_max_per_trade_pct: number
  risk_max_daily_loss_pct: number
  risk_max_open_positions: number
  /** Plafond du total en jeu si toutes mes cibles étaient atteintes. */
  risk_max_target_total: number
  risk_max_volume_lots: number
  /** Ratio gain/risque maximum de la saisie (TP saisi / SL saisi). */
  risk_max_reward_ratio: number
  /** Politique de protection : 'both' | 'stop_loss' | 'take_profit' | 'at_least_one' | 'optional'. */
  required_protection: string
  /** Ce qui doit exister chez le broker, ex. « Stop Loss et Take Profit sur l'ordre envoyé ». */
  required_protection_broker_side: string
  /** Ce que cela impose de remplir dans le formulaire. Toujours affiché avec la face ci-dessus. */
  required_protection_entry_side: string
}

/** Événement émis quand le backend refuse la session (401). */
export const SESSION_EXPIRED = 'reversetrader:session-expired'

/** Erreur renvoyée par le backend, avec son code HTTP. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Extrait un message lisible d'une réponse d'erreur.
 *
 * FastAPI renvoie deux formes distinctes : `{detail: "message"}` pour nos
 * erreurs métier, et `{detail: [{loc, msg}]}` pour les erreurs de validation
 * (422). Sans ce traitement, la seconde s'afficherait « [object Object] ».
 */
async function readErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      detail?: string | Array<{ loc?: (string | number)[]; msg?: string }>
    }
    if (typeof body.detail === 'string') return body.detail
    if (Array.isArray(body.detail)) {
      return body.detail
        .map((item) => {
          const field = item.loc?.filter((part) => part !== 'body').join('.')
          return field ? `${field} : ${item.msg}` : (item.msg ?? '')
        })
        .filter(Boolean)
        .join(' · ')
    }
  } catch {
    // Réponse non-JSON (page d'erreur du proxy par exemple).
  }
  return `${response.status} ${response.statusText}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // Le cookie de session est `HttpOnly` : le navigateur doit l'attacher
    // lui-même, le JavaScript n'y a pas accès (et c'est voulu).
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  })

  if (!response.ok) {
    if (response.status === 401) {
      // La session a expiré en cours d'utilisation. Sans ce signal,
      // l'interface enchaînerait les messages d'erreur incompréhensibles au
      // lieu de simplement redemander le mot de passe.
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED))
    }
    throw new ApiError(await readErrorDetail(response), response.status)
  }

  // 204 : réponse volontairement vide (suppression). La lire comme du JSON
  // lèverait une erreur sur une opération réussie.
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  health: () => request<Health>('/api/health'),

  // --- Session ------------------------------------------------------------
  session: () => request<SessionInfo>('/api/auth/session'),
  login: (username: string, password: string) =>
    request<SessionInfo>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<SessionInfo>('/api/auth/logout', { method: 'POST' }),

  config: () => request<PublicConfig>('/api/config'),

  // --- Administration -----------------------------------------------------
  users: () => request<ManagedUser[]>('/api/admin/users'),
  createUser: (body: CreateUserBody) =>
    request<ManagedUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: number, body: UpdateUserBody) =>
    request<ManagedUser>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  brokerAccounts: () => request<BrokerAccountOption[]>('/api/admin/broker-accounts'),

  // --- Comptes de trading ---------------------------------------------------
  /** Mes comptes, et celui qui est actif. */
  accounts: () => request<AccountsState>('/api/accounts'),
  /** Change le compte actif : tout ce qui suit — écran ET ordres — porte sur lui. */
  setActiveAccount: (accountId: number) =>
    request<AccountsState>('/api/accounts/active', {
      method: 'PUT',
      body: JSON.stringify({ account_id: accountId }),
    }),

  /** Ferme et rouvre la liaison avec le courtier du compte actif. */
  reconnectActive: () =>
    request<AccountsState>('/api/accounts/active/reconnect', { method: 'POST' }),

  tradingAccounts: () => request<TradingAccountAdmin[]>('/api/admin/trading-accounts'),
  createTradingAccount: (body: { external_id: string; label: string }) =>
    request<TradingAccountAdmin>('/api/admin/trading-accounts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  renameTradingAccount: (id: number, label: string) =>
    request<TradingAccountAdmin>(`/api/admin/trading-accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    }),
  deleteTradingAccount: (id: number) =>
    request<void>(`/api/admin/trading-accounts/${id}`, { method: 'DELETE' }),

  // --- cTrader : FTMO et les autres prop firms ------------------------------
  ctraderStatus: () => request<CTraderStatus>('/api/admin/ctrader'),
  saveCtraderApp: (body: { client_id: string; client_secret: string }) =>
    request<CTraderStatus>('/api/admin/ctrader/app', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  /** Adresse de la page cTrader où le titulaire autorise l'application. */
  ctraderAuthorize: () =>
    request<{ url: string }>('/api/admin/ctrader/authorize', { method: 'POST' }),
  deleteCtraderConnection: (id: number) =>
    request<void>(`/api/admin/ctrader/connections/${id}`, { method: 'DELETE' }),

  /** Statistiques de la stratégie : positions en cours, historique, journal. */
  stats: (days = 30, activityDays = 2) =>
    request<StatsResponse>(`/api/stats?days=${days}&activity_days=${activityDays}`),

  instruments: () => request<InstrumentsResponse>('/api/instruments'),

  /** Prix à saisir pour pouvoir calculer sur cette paire. */
  requiredQuotes: (symbol: string, currency: string) =>
    request<string[]>(
      `/api/calculator/required-quotes?symbol=${encodeURIComponent(symbol)}` +
        `&currency=${encodeURIComponent(currency)}`,
    ),

  brokerStatus: () => request<BrokerStatus>('/api/broker/status'),

  positions: () => request<Position[]>('/api/broker/positions'),

  /** Déplace le stop et la cible d'une position DÉJÀ ouverte. */
  amendPosition: (positionId: number, body: { stop_loss?: string; take_profit?: string }) =>
    request<unknown>(`/api/broker/positions/${positionId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  guardian: () => request<GuardianStatus>('/api/broker/guardian'),
  rearmGuardian: () => request<GuardianStatus>('/api/broker/guardian/rearm', { method: 'POST' }),
  /** Kill switch manuel : ferme TOUT et bloque les envois. */
  tripGuardian: () => request<GuardianStatus>('/api/broker/guardian/trip', { method: 'POST' }),

  /** Ferme une position. Volume absent = fermeture totale. */
  closePosition: (positionId: number, lots?: string) =>
    request<{ closed: number }>(`/api/broker/positions/${positionId}/close`, {
      method: 'POST',
      body: JSON.stringify({ lots: lots ?? null }),
    }),

  /** Chandeliers du broker. Échoue en 503 si le broker n'est pas connecté. */
  trendbars: (symbol: string, period: string, count = 300) =>
    request<Candle[]>(
      `/api/broker/trendbars?symbol=${encodeURIComponent(symbol)}` +
        `&period=${encodeURIComponent(period)}&count=${count}`,
    ),

  /**
   * ENVOIE l'ordre inversé au courtier. Engage de l'argent.
   *
   * `client_order_id` rend l'appel idempotent : un double appui ou un renvoi
   * après une réponse perdue ne produit pas deux ordres.
   */
  sendOrder: (body: PlanRequest & { confirmed: boolean; client_order_id: string }) =>
    request<OrderResponse>('/api/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Calcule l'ordre inversé. N'envoie RIEN au broker. */
  plan: (body: PlanRequest, signal?: AbortSignal) =>
    request<PlanResponse>('/api/calculator/plan', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    }),
}
